import {
  getFinanceInformation,
  getWWFeeInformation,
  updateFinanceInformation,
  sendDirectDebitNotifications,
} from "../../api/finance";
import { getLastWW } from "../../api/events";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { SendDirectDebitNotificationsDto, UpdateFinanceInformationDto } from "../../types/membersTypes";
import { showSuccessMessage } from "../../utils/toastUtils";

const useFinance = () => {
  const queryClient = useQueryClient();

  // ############
  // GET QUERIES
  // ############

  // getFinanceInformation query
  const { data: financeInformation } = useQuery({
    queryKey: ["financeInformation"],
    queryFn: getFinanceInformation,
  });
  const memberFinanceInformation = financeInformation ? financeInformation.data : [];

  // getWWFeeInformation query
  const { data: wwFeeInformation } = useQuery({
    queryKey: ["wwFeeInformation"],
    queryFn: getWWFeeInformation,
  });
  const memberWWFeeInformation = wwFeeInformation ? wwFeeInformation.data : [];

  // getLastWW query
  const { data: lastWWData } = useQuery({
    queryKey: ["lastWW"],
    queryFn: getLastWW,
  });
  const lastWW = lastWWData ? lastWWData.data : null;

  // ----------------------------------------------------------------------------------
  // ############
  // UPDATE MUTATIONS
  // ############

  const { mutateAsync: updateFinance } = useMutation({
    mutationFn: (params: { memberId: number; updateData: UpdateFinanceInformationDto }) =>
      updateFinanceInformation(params.memberId, params.updateData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["financeInformation"] });
      showSuccessMessage("Finanzinformationen erfolgreich aktualisiert");
    },
  });

  /**
   * Updates the finance information for a specific member by calling the updateFinance mutation with the provided member ID and update data.
   * @param memberId The ID of the member whose finance information is to be updated
   * @param updateData The finance information to update, of type UpdateFinanceInformationDto
   */
  const updateFinanceInformationForMember = async (memberId: number, updateData: UpdateFinanceInformationDto) => {
    await updateFinance({ memberId, updateData });
  };

  const { mutateAsync: sendDirectDebitNotificationsMutation, isPending: isSendingDirectDebitNotifications } =
    useMutation({
      mutationFn: (dto: SendDirectDebitNotificationsDto) => sendDirectDebitNotifications(dto),
      onSuccess: async (data, variables) => {
        if (variables.sendEmails && data.failed.length === 0) {
          showSuccessMessage("Benachrichtigungs-E-Mails wurden versendet");
        }
      },
    });

  /**
   * Sends SEPA direct debit notification emails for the given recipients.
   * @returns Report of notified and failed members
   */
  const sendDirectDebitNotificationsForMembers = async (dto: SendDirectDebitNotificationsDto) => {
    return await sendDirectDebitNotificationsMutation(dto);
  };

  return {
    memberFinanceInformation,
    memberWWFeeInformation,
    lastWW,
    updateFinance,
    updateFinanceInformationForMember,
    sendDirectDebitNotificationsForMembers,
    isSendingDirectDebitNotifications,
  };
};

export default useFinance;
